import type { Editor } from 'tinymce';
import { pickImageForWysiwyg } from './tinymceImagePicker';

/** Couleurs de texte (palette éditoriale LMS). */
export const TINYMCE_TEXT_COLORS = [
  { hex: 'FF4D66', label: 'Rouge' },
  { hex: '00D377', label: 'Vert' },
  { hex: '6154FE', label: 'Bleu' },
  { hex: 'FFBA00', label: 'Jaune' },
] as const;

/**
 * Surlignage : TinyMCE ne gère que l'hex 3/6 caractères dans color_map (pas #RRGGBBAA).
 * On utilise des teintes pré-mélangées à ~20 % sur blanc (255×0,8 + canal×0,2).
 */
export const TINYMCE_HIGHLIGHT_COLORS = [
  { hex: 'FFDBE0', label: 'Rouge' },
  { hex: 'CCF6E4', label: 'Vert' },
  { hex: 'DFDDFF', label: 'Bleu' },
  { hex: 'FFF1CC', label: 'Jaune' },
] as const;

const toColorMap = (colors: ReadonlyArray<{ hex: string; label: string }>) =>
  colors.flatMap((c) => [c.hex, c.label]);

export const tinymceColorMapForeground = toColorMap(TINYMCE_TEXT_COLORS);
export const tinymceColorMapBackground = toColorMap(TINYMCE_HIGHLIGHT_COLORS);

const registerChecklistButton = (editor: Editor) => {
  const toggleChecklistItem = (li: HTMLElement) => {
    editor.undoManager.transact(() => {
      if (li.classList.contains('tox-checklist--checked')) {
        li.classList.remove('tox-checklist--checked');
      } else {
        li.classList.add('tox-checklist--checked');
      }
    });
    editor.nodeChanged();
  };

  editor.on('click', (event) => {
    const mouseEvent = event as MouseEvent;
    const target = mouseEvent.target as HTMLElement | null;
    if (!target) return;
    const li = target.closest('ul.tox-checklist > li') as HTMLElement | null;
    if (!li || !editor.dom.isEditable(li)) return;
    const liRect = li.getBoundingClientRect();
    // Ne cocher/décocher que sur la zone de la case (gauche), pas sur le texte.
    if (mouseEvent.clientX > liRect.left + 28) return;
    mouseEvent.preventDefault();
    toggleChecklistItem(li);
  });

  editor.on('keydown', (event) => {
    if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey)) return;
    const node = editor.selection.getNode();
    const li = editor.dom.getParent(node, 'ul.tox-checklist > li') as HTMLElement | null;
    if (!li) return;
    event.preventDefault();
    toggleChecklistItem(li);
  });

  editor.ui.registry.addToggleButton('checklist', {
    icon: 'checklist',
    tooltip: 'Liste à cocher',
    onAction: () => {
      const node = editor.selection.getNode();
      const existing = editor.dom.getParent(node, 'ul.tox-checklist');
      if (existing) {
        editor.dom.removeClass(existing, 'tox-checklist');
        editor.dom.select('li', existing).forEach((li) => {
          editor.dom.removeClass(li, 'tox-checklist--checked');
        });
        return;
      }
      editor.insertContent('<ul class="tox-checklist"><li></li></ul>');
    },
    onSetup: (api) => {
      const update = () => {
        const node = editor.selection.getNode();
        api.setActive(!!editor.dom.getParent(node, 'ul.tox-checklist'));
      };
      editor.on('NodeChange', update);
      return () => editor.off('NodeChange', update);
    },
  });
};

const insertUploadedImage = async (editor: Editor) => {
  const url = await pickImageForWysiwyg();
  if (!url) return;
  editor.insertContent(
    `<img src="${url}" alt="" style="max-width: 100%; height: auto;" />`
  );
};

const registerImagePickerButton = (editor: Editor) => {
  editor.ui.registry.addButton('image', {
    icon: 'image',
    tooltip: 'Insérer une image',
    onAction: () => {
      void insertUploadedImage(editor);
    },
  });
};

export const tinymceEditorSetup = (editor: Editor) => {
  registerChecklistButton(editor);
  registerImagePickerButton(editor);
};

const filePickerCallback = (
  callback: (url: string, meta?: { alt?: string }) => void,
  _value: string,
  meta: { filetype: string }
) => {
  if (meta.filetype !== 'image') return;
  void pickImageForWysiwyg().then((url) => {
    if (url) {
      callback(url, { alt: '' });
    }
  });
};

export const tinymceBaseInit = {
  menubar: false,
  branding: false,
  statusbar: false,
  plugins: 'lists advlist table link code image',
  toolbar: [
    'undo redo',
    'formatselect',
    'bold italic underline',
    'forecolor backcolor',
    'alignleft aligncenter alignright',
    'bullist numlist checklist',
    'table',
    'link image',
    'removeformat',
    'code',
  ].join(' | '),
  color_cols: 4,
  color_cols_foreground: 4,
  color_cols_background: 4,
  color_map_foreground: tinymceColorMapForeground,
  color_map_background: tinymceColorMapBackground,
  table_toolbar: 'tableprops tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol',
  valid_classes: {
    ul: 'tox-checklist',
    li: 'tox-checklist--checked tox-checklist--hidden',
  },
  content_style: [
    'body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; }',
    'img { max-width: 100%; height: auto; border-radius: 4px; }',
    'p { line-height: 1.6; }',
    'table { border-collapse: collapse; width: 100%; }',
    'table td, table th { border: 1px solid #ccc; padding: 6px 8px; }',
    '.tox-checklist > li:not(.tox-checklist--hidden) { list-style: none; margin: 0.25em 0; position: relative; }',
    '.tox-checklist > li:not(.tox-checklist--hidden)::before {',
    '  content: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2016%2016%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Crect%20width%3D%2215%22%20height%3D%2215%22%20x%3D%22.5%22%20y%3D%22.5%22%20stroke%3D%22%234C4C4C%22%20rx%3D%222%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E");',
    '  cursor: pointer; height: 1em; margin-left: -1.5em; margin-top: 0.125em; position: absolute; width: 1em;',
    '}',
    '.tox-checklist li:not(.tox-checklist--hidden).tox-checklist--checked::before {',
    '  content: url("data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2016%2016%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Crect%20width%3D%2216%22%20height%3D%2216%22%20fill%3D%22%236154FE%22%20rx%3D%222%22%2F%3E%3Cpath%20fill%3D%22%23FFF%22%20d%3D%22M11.57%2C3.14a1%2C1%2C0%200%2C1%2C1.41%2C0L13.09%2C4.14a1%2C1%2C0%200%2C1%2C0%2C1.41L7.76%2C11.86a1%2C1%2C0%200%2C1-1.4%2C0L3.02%2C8.81a1%2C1%2C0%200%2C1%2C1.41-1.41L6.85%2C9.97l4.72-6.83Z%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E");',
    '}',
  ].join(' '),
  file_picker_types: 'image',
  file_picker_callback: filePickerCallback,
};
